import { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

export type BrushMode = 'paint' | 'erase' | 'pan'
export type ViewMode = 'original' | 'mask' | 'cutout' | 'white' | 'black' | 'gray' | 'shadow' | 'compare'

interface Props {
  imageUrl: string
  maskUrl?: string | null
  overlayUrl?: string | null
  viewMode: ViewMode
  brushMode: BrushMode
  brushSize: number
  brushHardness: number
  brushOpacity: number
  compareRatio: number
  bgMode: 'checker' | 'white' | 'black' | 'gray'
  onMaskChange?: (dirty: boolean) => void
  maskRef?: React.MutableRefObject<HTMLCanvasElement | null>
}

export default function MaskCanvas({
  imageUrl,
  maskUrl,
  overlayUrl,
  viewMode,
  brushMode,
  brushSize,
  brushHardness,
  brushOpacity,
  compareRatio,
  bgMode,
  onMaskChange,
  maskRef,
}: Props) {
  const { t } = useTranslation('autoProcess')
  const hostRef = useRef<HTMLDivElement>(null)
  const imageCanvasRef = useRef<HTMLCanvasElement>(null)
  const maskCanvasRef = useRef<HTMLCanvasElement>(null)
  const viewCanvasRef = useRef<HTMLCanvasElement>(null)
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 })
  const drawing = useRef(false)
  const last = useRef<{ x: number; y: number } | null>(null)
  const history = useRef<ImageData[]>([])
  const historyIdx = useRef(-1)
  const panning = useRef(false)
  const panStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 })

  const pushHistory = useCallback(() => {
    const c = maskCanvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')!
    const data = ctx.getImageData(0, 0, c.width, c.height)
    history.current = history.current.slice(0, historyIdx.current + 1)
    history.current.push(data)
    if (history.current.length > 40) history.current.shift()
    historyIdx.current = history.current.length - 1
  }, [])

  const undo = useCallback(() => {
    const c = maskCanvasRef.current
    if (!c || historyIdx.current <= 0) return
    historyIdx.current -= 1
    const ctx = c.getContext('2d')!
    ctx.putImageData(history.current[historyIdx.current], 0, 0)
    onMaskChange?.(true)
    redraw()
  }, [onMaskChange])

  const redo = useCallback(() => {
    const c = maskCanvasRef.current
    if (!c || historyIdx.current >= history.current.length - 1) return
    historyIdx.current += 1
    const ctx = c.getContext('2d')!
    ctx.putImageData(history.current[historyIdx.current], 0, 0)
    onMaskChange?.(true)
    redraw()
  }, [onMaskChange])

  // expose undo/redo via custom events
  useEffect(() => {
    const onUndo = () => undo()
    const onRedo = () => redo()
    window.addEventListener('editor-undo', onUndo)
    window.addEventListener('editor-redo', onRedo)
    return () => {
      window.removeEventListener('editor-undo', onUndo)
      window.removeEventListener('editor-redo', onRedo)
    }
  }, [undo, redo])

  useEffect(() => {
    if (maskRef) maskRef.current = maskCanvasRef.current
  })

  useEffect(() => {
    let cancelled = false
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = async () => {
      if (cancelled) return
      setImgSize({ w: img.width, h: img.height })
      const ic = imageCanvasRef.current!
      const mc = maskCanvasRef.current!
      const vc = viewCanvasRef.current!
      for (const c of [ic, mc, vc]) {
        c.width = img.width
        c.height = img.height
      }
      const ictx = ic.getContext('2d')!
      ictx.clearRect(0, 0, img.width, img.height)
      ictx.drawImage(img, 0, 0)

      const mctx = mc.getContext('2d')!
      mctx.clearRect(0, 0, img.width, img.height)
      if (maskUrl) {
        const mimg = await loadImage(maskUrl)
        // mask may be L or RGBA — draw and extract
        mctx.drawImage(mimg, 0, 0, img.width, img.height)
        const data = mctx.getImageData(0, 0, img.width, img.height)
        // Normalize to alpha in R channel for painting
        for (let i = 0; i < data.data.length; i += 4) {
          const a = data.data[i + 3] < 255 ? data.data[i + 3] : data.data[i]
          data.data[i] = 255
          data.data[i + 1] = 255
          data.data[i + 2] = 255
          data.data[i + 3] = a
        }
        mctx.putImageData(data, 0, 0)
      } else {
        mctx.fillStyle = 'rgba(255,255,255,0)'
        mctx.fillRect(0, 0, img.width, img.height)
      }
      history.current = []
      historyIdx.current = -1
      pushHistory()
      fitToView(img.width, img.height)
      redraw()
    }
    img.src = imageUrl
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, maskUrl])

  useEffect(() => {
    redraw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, overlayUrl, compareRatio, bgMode, scale, offset, imgSize])

  function fitToView(w = imgSize.w, h = imgSize.h) {
    const host = hostRef.current
    if (!host || !w || !h) return
    const pad = 24
    const sx = (host.clientWidth - pad) / w
    const sy = (host.clientHeight - pad) / h
    const s = Math.min(sx, sy, 1)
    setScale(s)
    setOffset({
      x: (host.clientWidth - w * s) / 2,
      y: (host.clientHeight - h * s) / 2,
    })
  }

  function redraw() {
    const vc = viewCanvasRef.current
    const ic = imageCanvasRef.current
    const mc = maskCanvasRef.current
    if (!vc || !ic || !mc || !imgSize.w) return
    const ctx = vc.getContext('2d')!
    ctx.clearRect(0, 0, vc.width, vc.height)

    // background
    if (bgMode === 'white') ctx.fillStyle = '#fff'
    else if (bgMode === 'black') ctx.fillStyle = '#000'
    else if (bgMode === 'gray') ctx.fillStyle = '#808080'
    else ctx.clearRect(0, 0, vc.width, vc.height)
    if (bgMode !== 'checker') ctx.fillRect(0, 0, vc.width, vc.height)

    if (viewMode === 'original') {
      ctx.drawImage(ic, 0, 0)
      return
    }

    if (viewMode === 'mask') {
      ctx.fillStyle = '#222'
      ctx.fillRect(0, 0, vc.width, vc.height)
      ctx.drawImage(mc, 0, 0)
      return
    }

    // compose cutout locally from image + mask
    const tmp = document.createElement('canvas')
    tmp.width = vc.width
    tmp.height = vc.height
    const tctx = tmp.getContext('2d')!
    tctx.drawImage(ic, 0, 0)
    tctx.globalCompositeOperation = 'destination-in'
    tctx.drawImage(mc, 0, 0)

    if (viewMode === 'cutout' || viewMode === 'compare') {
      if (viewMode === 'compare') {
        ctx.drawImage(ic, 0, 0)
        const w = Math.floor(vc.width * compareRatio)
        ctx.save()
        ctx.beginPath()
        ctx.rect(0, 0, w, vc.height)
        ctx.clip()
        if (bgMode === 'white') {
          ctx.fillStyle = '#fff'
          ctx.fillRect(0, 0, vc.width, vc.height)
        }
        ctx.drawImage(tmp, 0, 0)
        ctx.restore()
        ctx.strokeStyle = '#1f6f5b'
        ctx.lineWidth = 2 / scale
        ctx.beginPath()
        ctx.moveTo(w, 0)
        ctx.lineTo(w, vc.height)
        ctx.stroke()
      } else {
        ctx.drawImage(tmp, 0, 0)
      }
      return
    }

    if (['white', 'black', 'gray'].includes(viewMode)) {
      const colors: Record<string, string> = { white: '#fff', black: '#000', gray: '#808080' }
      ctx.fillStyle = colors[viewMode]
      ctx.fillRect(0, 0, vc.width, vc.height)
      ctx.drawImage(tmp, 0, 0)
      return
    }

    if (viewMode === 'shadow' && overlayUrl) {
      loadImage(overlayUrl).then((o) => {
        ctx.drawImage(o, 0, 0, vc.width, vc.height)
      })
      return
    }

    ctx.drawImage(tmp, 0, 0)
  }

  function toImageCoords(e: React.PointerEvent) {
    const host = hostRef.current!
    const rect = host.getBoundingClientRect()
    const x = (e.clientX - rect.left - offset.x) / scale
    const y = (e.clientY - rect.top - offset.y) / scale
    return { x, y }
  }

  function paintAt(x: number, y: number) {
    const mc = maskCanvasRef.current
    if (!mc) return
    const ctx = mc.getContext('2d')!
    const r = brushSize / 2
    const soft = Math.max(0.01, 1 - brushHardness)
    const grad = ctx.createRadialGradient(x, y, r * (1 - soft), x, y, r)
    const alpha = brushOpacity
    if (brushMode === 'paint') {
      grad.addColorStop(0, `rgba(255,255,255,${alpha})`)
      grad.addColorStop(1, 'rgba(255,255,255,0)')
      ctx.globalCompositeOperation = 'source-over'
      ctx.fillStyle = grad
      ctx.beginPath()
      ctx.arc(x, y, r, 0, Math.PI * 2)
      ctx.fill()
    } else if (brushMode === 'erase') {
      ctx.globalCompositeOperation = 'destination-out'
      grad.addColorStop(0, `rgba(0,0,0,${alpha})`)
      grad.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = grad
      ctx.beginPath()
      ctx.arc(x, y, r, 0, Math.PI * 2)
      ctx.fill()
      ctx.globalCompositeOperation = 'source-over'
    }
  }

  function onPointerDown(e: React.PointerEvent) {
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    if (brushMode === 'pan' || e.button === 1) {
      panning.current = true
      panStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y }
      return
    }
    if (!['paint', 'erase'].includes(brushMode)) return
    drawing.current = true
    const p = toImageCoords(e)
    last.current = p
    paintAt(p.x, p.y)
    onMaskChange?.(true)
    redraw()
  }

  function onPointerMove(e: React.PointerEvent) {
    if (panning.current) {
      setOffset({
        x: panStart.current.ox + (e.clientX - panStart.current.x),
        y: panStart.current.oy + (e.clientY - panStart.current.y),
      })
      return
    }
    if (!drawing.current || !last.current) return
    const p = toImageCoords(e)
    const dist = Math.hypot(p.x - last.current.x, p.y - last.current.y)
    const steps = Math.max(1, Math.floor(dist / (brushSize * 0.25)))
    for (let i = 1; i <= steps; i++) {
      const t = i / steps
      paintAt(last.current.x + (p.x - last.current.x) * t, last.current.y + (p.y - last.current.y) * t)
    }
    last.current = p
    redraw()
  }

  function onPointerUp() {
    if (drawing.current) {
      drawing.current = false
      last.current = null
      pushHistory()
    }
    panning.current = false
  }

  function onWheel(e: React.WheelEvent) {
    e.preventDefault()
    const factor = e.deltaY > 0 ? 0.9 : 1.1
    setScale((s) => Math.min(8, Math.max(0.05, s * factor)))
  }

  const transform = `translate(${offset.x}px, ${offset.y}px) scale(${scale})`
  const hostClass = `mask-canvas-host ${bgMode === 'checker' ? 'checkerboard' : ''}`

  return (
    <div
      ref={hostRef}
      className={hostClass}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onWheel={onWheel}
      style={{ cursor: brushMode === 'pan' ? 'grab' : 'crosshair' }}
    >
      <canvas ref={imageCanvasRef} style={{ display: 'none' }} />
      <canvas ref={maskCanvasRef} style={{ display: 'none' }} />
      <canvas ref={viewCanvasRef} style={{ transform, width: imgSize.w, height: imgSize.h }} />
      <div style={{ position: 'absolute', right: 10, bottom: 10, color: '#aaa', fontSize: 12 }}>
        {Math.round(scale * 100)}% · {imgSize.w}×{imgSize.h}
        <button type="button" style={{ marginLeft: 8 }} onClick={() => fitToView()}>
          {t('cutoutEditor.fitView')}
        </button>
        <button type="button" style={{ marginLeft: 4 }} onClick={() => setScale(1)}>
          100%
        </button>
      </div>
    </div>
  )
}

function loadImage(url: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

export async function exportMaskBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  // Export as PNG with alpha channel (L-like via alpha)
  const out = document.createElement('canvas')
  out.width = canvas.width
  out.height = canvas.height
  const ctx = out.getContext('2d')!
  const src = canvas.getContext('2d')!.getImageData(0, 0, canvas.width, canvas.height)
  const dst = ctx.createImageData(canvas.width, canvas.height)
  for (let i = 0; i < src.data.length; i += 4) {
    const a = src.data[i + 3]
    dst.data[i] = a
    dst.data[i + 1] = a
    dst.data[i + 2] = a
    dst.data[i + 3] = 255
  }
  ctx.putImageData(dst, 0, 0)
  return new Promise((resolve, reject) => {
    out.toBlob((b) => (b ? resolve(b) : reject(new Error('export failed'))), 'image/png')
  })
}
